import { EVENT_TYPES } from '@trip-planner-monorepo/shared';
// Static imports for accommodation-day utilities
// NOTE: These were changed from dynamic imports to static imports to resolve HMR (Hot Module Replacement) 
// conflicts during development. Dynamic imports were causing module loading issues when the suggestion 
// rules were re-evaluated. Static imports are appropriate here because these utilities are always needed
// when the suggestion rules execute, so there's no performance benefit from lazy loading.
import { accommodationDayUtils } from '@trip-planner-monorepo/days';
const {
  calculateAccommodationDateRangeForDaysSuggestions,
  findMissingDaysForAccommodationDates,
  generateDayContentForAccommodationSuggestions,
  validateAccommodationDatesForDayCreation,
  calculateDaysListDateRange,
  formatDateForDisplay
} = accommodationDayUtils;
// Static import for accommodation service
// NOTE: Changed from dynamic import to static import for HMR stability and consistency with
// existing codebase patterns. This service is frequently used in suggestion rules.
import { accommodationService } from '@trip-planner-monorepo/accommodation';
// Import placesService if you need to directly call it within a rule, e.g., for complex lookups not handled by the main service.
// However, the design passes placesService in the context object to rules, so direct import here might not be needed for this rule.
// import { placesService } from '@trip-planner-monorepo/places';

/**
 * Helper function to detect exact matches between input city name and existing places
 * @param {string} inputCityName - The city name to check for exact matches
 * @param {Array} places - Array of existing places to check against
 * @returns {boolean} - True if an exact match is found
 */
const hasExactMatch = (inputCityName, places) => {
  const normalizedInput = inputCityName.trim().toLowerCase();
    
  return places.some(place => {
    const placeName = place.name?.trim().toLowerCase() || '';
    const placeFullName = place.fullName?.trim().toLowerCase() || '';
        
    // Check for exact match with place name or full name
    return placeName === normalizedInput || placeFullName === normalizedInput;
  });
};

export const suggestionRules = [
  {
    ruleName: 'SuggestPlaceForNewAccommodation',
    eventType: EVENT_TYPES.ACCOMMODATION_ADDED,
    /**
         * @param {Object} eventData - The event data for ACCOMMODATION_ADDED.
         * @param {Object} context - Context object, contains { placesService }.
         * @returns {Promise<boolean>} - True if conditions are met, false otherwise.
         */
    conditions: async (eventData, context) => {
      // Condition: Event must have city information.
      if (!eventData.details?.city) {
        console.log('Rule \'SuggestPlaceForNewAccommodation\': No city info in eventData.details, skipping.', eventData);
        return false;
      }
      // Condition: Event must have tripId and userId for fetching/creating places.
      if (!eventData.tripId || !eventData.userId) {
        console.log('Rule \'SuggestPlaceForNewAccommodation\': Missing tripId or userId in eventData, skipping.', eventData);
        return false;
      }
      console.log('Rule \'SuggestPlaceForNewAccommodation\': Conditions met for eventData:', eventData);
      return true;
    },
    /**
         * @param {Object} eventData - The event data for ACCOMMODATION_ADDED.
         * @param {Object} context - Context object, contains { placesService }.
         * @returns {Promise<Object>} - Object containing suggestionType, messageTemplateKey, and data for the suggestion.
         */
    generateSuggestionDetails: async (eventData, context) => {
      const { id: accommodationId, tripId, userId, details, summary } = eventData;
      const accommodationName = summary || details.name || 'New Accommodation';
      const cityName = details.city;
      const countryName = details.country || '';

      console.log(`Rule 'SuggestPlaceForNewAccommodation': Processing accommodation in: "${cityName}"`);

      try {
        // Extract just the city name (before comma, colon, semicolon)
        const extractedCityName = cityName.split(/[,:;]/)[0].trim();
        console.log(`Rule 'SuggestPlaceForNewAccommodation': Extracted city name: "${extractedCityName}"`);

        // Get all places and find ones with city name matches using fuzzy matching
        // This provides more options for user selection since user makes final choice
        const allPlaces = await context.placesService.fetchPlaces(userId, tripId);
        const cityMatches = allPlaces.filter(place => {
          const placeCityName = place.name.split(/[,:;]/)[0].trim().toLowerCase();
          const fullNameCityName = place.fullName ? place.fullName.split(/[,:;]/)[0].trim().toLowerCase() : '';
          const targetCity = extractedCityName.toLowerCase();
                    
          // Check for exact matches or very close matches on city name only
          return placeCityName === targetCity || 
                           fullNameCityName === targetCity ||
                           placeCityName.includes(targetCity) || 
                           targetCity.includes(placeCityName);
        });

        console.log(`Rule 'SuggestPlaceForNewAccommodation': Found ${cityMatches.length} city matches for "${extractedCityName}"`);

        if (cityMatches.length === 0) {
          // No city matches found - suggest creation
          console.log(`Rule 'SuggestPlaceForNewAccommodation': No city matches found for "${extractedCityName}". Will suggest creation.`);
                    
          return {
            suggestionType: 'PLACE_LINK_OR_CREATE_FROM_ACCOMMODATION',
            messageTemplateKey: 'create_new_place_for_accommodation',
            uiTitle: `Create place for ${cityName}?`,
            uiMessage: `You added accommodation "${accommodationName}" in ${cityName}. Would you like to create a new place entry for ${cityName}?`,
            data: {
              cityName,
              country: countryName,
              accommodationId,
              tripId,
              userId,
              accommodationName,
              needsCreation: true,
              existingPlaceId: null,
              existingPlaceName: null,
              placeCreationData: {
                name: cityName,
                fullName: countryName ? `${cityName}, ${countryName}` : cityName,
              }
            }
          };
        } else {
          // City matches found - show disambiguation (even for single matches)
          console.log('Rule \'SuggestPlaceForNewAccommodation\': Found city matches, showing disambiguation');
                    
          // Check if there's an exact match to determine whether to include "create new place" option
          const exactMatchExists = hasExactMatch(cityName, cityMatches);
          console.log(`Rule 'SuggestPlaceForNewAccommodation': Exact match exists for "${cityName}": ${exactMatchExists}`);
                    
          const suggestionData = {
            suggestionType: 'PLACE_DISAMBIGUATION_REQUIRED',
            messageTemplateKey: 'disambiguate_place_for_accommodation',
            uiTitle: `Choose place for ${cityName}`,
            uiMessage: exactMatchExists ? 
              `You added a new accommodation in "${cityName}". We found existing places that match. Please choose one:` :
              `You added a new accommodation in "${cityName}". We found existing places that might match. Please choose one or create a new place:`,
            data: {
              cityName,
              country: countryName,
              accommodationId,
              tripId,
              userId,
              accommodationName,
              similarPlaces: cityMatches.map(place => ({
                ...place,
                similarityScore: 1.0 // All are considered good matches since we use fuzzy city name matching
              }))
            }
          };
                    
          // Only include newPlaceOption if no exact match exists
          if (!exactMatchExists) {
            suggestionData.data.newPlaceOption = {
              name: cityName,
              fullName: countryName ? `${cityName}, ${countryName}` : cityName,
            };
          }
                    
          return suggestionData;
        }
      } catch (error) {
        console.error('Rule \'SuggestPlaceForNewAccommodation\': Error during city matching:', error);
        // Fallback to suggesting creation if city matching fails
        return {
          suggestionType: 'PLACE_LINK_OR_CREATE_FROM_ACCOMMODATION',
          messageTemplateKey: 'create_new_place_for_accommodation',
          uiTitle: `Create place for ${cityName}?`,
          uiMessage: `You added accommodation "${accommodationName}" in ${cityName}. Would you like to create a new place entry for ${cityName}?`,
          data: {
            cityName,
            country: countryName,
            accommodationId,
            tripId,
            userId,
            accommodationName,
            needsCreation: true,
            existingPlaceId: null,
            existingPlaceName: null,
            placeCreationData: {
              name: cityName,
              fullName: countryName ? `${cityName}, ${countryName}` : cityName,
            }
          }
        };
      }
    },
    notificationStrategy: 'immediate_prompt',
    priority: 'high'
  },
  {
    ruleName: 'SuggestPlaceForChangedAccommodationCity',
    eventType: EVENT_TYPES.ACCOMMODATION_CITY_CHANGED,
    /**
         * @param {Object} eventData - The event data for ACCOMMODATION_CITY_CHANGED.
         * @param {Object} context - Context object, contains { placesService }.
         * @returns {Promise<boolean>} - True if conditions are met, false otherwise.
         */
    conditions: async (eventData, context) => {
      // Condition: Event must have new city information
      if (!eventData.details?.city) {
        console.log('Rule \'SuggestPlaceForChangedAccommodationCity\': No new city info in eventData.details, skipping.', eventData);
        return false;
      }
      // Condition: Event must have tripId and userId for fetching/creating places
      if (!eventData.tripId || !eventData.userId) {
        console.log('Rule \'SuggestPlaceForChangedAccommodationCity\': Missing tripId or userId in eventData, skipping.', eventData);
        return false;
      }
      // Condition: City must have actually changed
      if (eventData.details.city === eventData.oldCity) {
        console.log('Rule \'SuggestPlaceForChangedAccommodationCity\': City hasn\'t changed, skipping.', eventData);
        return false;
      }
      console.log('Rule \'SuggestPlaceForChangedAccommodationCity\': Conditions met for eventData:', eventData);
      return true;
    },
    /**
         * @param {Object} eventData - The event data for ACCOMMODATION_CITY_CHANGED.
         * @param {Object} context - Context object, contains { placesService }.
         * @returns {Promise<Object>} - Object containing suggestionType, messageTemplateKey, and data for the suggestion.
         */
    generateSuggestionDetails: async (eventData, context) => {
      const { id: accommodationId, tripId, userId, details, summary, oldCity, oldPlaceReference } = eventData;
      const accommodationName = summary || details.name || 'Accommodation';
      const newCityName = details.city;
      const countryName = details.country || '';

      console.log(`Rule 'SuggestPlaceForChangedAccommodationCity': Processing city change from "${oldCity}" to "${newCityName}"`);
      console.log('Rule eventData received:', JSON.stringify(eventData, null, 2));

      try {
        // Extract just the city name (before comma, colon, semicolon)
        const extractedCityName = newCityName.split(/[,:;]/)[0].trim();
        console.log(`Rule 'SuggestPlaceForChangedAccommodationCity': Extracted city name: "${extractedCityName}"`);

        // Get all places and find ones with city name matches using fuzzy matching
        // This provides more options for user selection since user makes final choice
        const allPlaces = await context.placesService.fetchPlaces(userId, tripId);
        const cityMatches = allPlaces.filter(place => {
          const placeCityName = place.name.split(/[,:;]/)[0].trim().toLowerCase();
          const placeFullNameCityName = place.fullName ? place.fullName.split(/[,:;]/)[0].trim().toLowerCase() : '';
          const targetCity = extractedCityName.toLowerCase();
                    
          // Use same fuzzy matching logic as first rule to provide more options for user selection
          return placeCityName === targetCity || 
                           placeFullNameCityName === targetCity ||
                           placeCityName.includes(targetCity) || 
                           targetCity.includes(placeCityName);
        });

        console.log(`Rule 'SuggestPlaceForChangedAccommodationCity': Found ${cityMatches.length} city name matches for "${extractedCityName}"`);
        cityMatches.forEach(match => {
          console.log(`  - "${match.name}" (${match.fullName || 'no full name'})`);
        });

        // If old place exists, check if it has other references
        let oldPlaceHasOtherReferences = false;
        if (oldPlaceReference) {
          const oldPlaceUsageCount = await context.placesService.getPlaceUsageCount(userId, tripId, oldPlaceReference.placeId);
          oldPlaceHasOtherReferences = oldPlaceUsageCount > 1;
          console.log(`Rule 'SuggestPlaceForChangedAccommodationCity': Old place ${oldPlaceReference.placeId} has ${oldPlaceUsageCount} references (other references: ${oldPlaceHasOtherReferences})`);
        }

        if (cityMatches.length === 0) {
          // No city matches found - suggest creation
          console.log(`Rule 'SuggestPlaceForChangedAccommodationCity': No city matches found for "${extractedCityName}". Will suggest creation.`);
                    
          return {
            suggestionType: 'PLACE_LINK_OR_CREATE_FROM_CITY_CHANGE',
            messageTemplateKey: 'create_new_place_for_changed_city',
            uiTitle: `Create place for ${newCityName}?`,
            uiMessage: `You changed the city for "${accommodationName}" from "${oldCity}" to "${newCityName}". Would you like to create a new place entry for ${newCityName}?`,
            data: {
              newCityName,
              oldCityName: oldCity,
              country: countryName,
              accommodationId,
              tripId,
              userId,
              accommodationName,
              needsCreation: true,
              existingPlaceId: null,
              existingPlaceName: null,
              placeCreationData: {
                name: newCityName,
                fullName: newCityName,
                context: countryName,
                type: 'city',
                userContent: `Place created for accommodation: ${accommodationName}`
              },
              oldPlaceReference,
              oldPlaceHasOtherReferences,
              suggestOldPlaceCleanup: oldPlaceReference && !oldPlaceHasOtherReferences
            }
          };
        } else {
          // City matches found - always show disambiguation (even for single matches)
          // Users select the exact place by ID - no text-based assumptions
          console.log('Rule \'SuggestPlaceForChangedAccommodationCity\': Found city matches, showing disambiguation');
                    
          // Check if there's an exact match to determine whether to include "create new place" option
          const exactMatchExists = hasExactMatch(newCityName, cityMatches);
          console.log(`Rule 'SuggestPlaceForChangedAccommodationCity': Exact match exists for "${newCityName}": ${exactMatchExists}`);
                    
          const suggestionData = {
            suggestionType: 'PLACE_DISAMBIGUATION_REQUIRED',
            messageTemplateKey: 'disambiguate_place_for_changed_city',
            uiTitle: `Choose place for ${newCityName}`,
            uiMessage: exactMatchExists ?
              `You changed the city for "${accommodationName}" from "${oldCity}" to "${newCityName}". We found existing places that match. Please choose one:` :
              `You changed the city for "${accommodationName}" from "${oldCity}" to "${newCityName}". We found existing places that might match. Please choose one or create a new place:`,
            data: {
              newCityName,
              oldCityName: oldCity,
              country: countryName,
              accommodationId,
              tripId,
              userId,
              accommodationName,
              similarPlaces: cityMatches.map(place => ({
                ...place,
                similarityScore: 1.0 // All are considered good matches since we use fuzzy city name matching
              })),
              oldPlaceReference,
              oldPlaceHasOtherReferences,
              suggestOldPlaceCleanup: oldPlaceReference && !oldPlaceHasOtherReferences
            }
          };
                    
          // Only include newPlaceOption if no exact match exists
          if (!exactMatchExists) {
            suggestionData.data.newPlaceOption = {
              name: newCityName,
              fullName: newCityName,
              context: countryName,
              type: 'city',
              userContent: `Place created for accommodation: ${accommodationName}`
            };
          }
                    
          return suggestionData;
        }
      } catch (error) {
        console.error('Rule \'SuggestPlaceForChangedAccommodationCity\': Error processing city change:', error);
        // Fallback to suggesting creation if something goes wrong
        return {
          suggestionType: 'PLACE_LINK_OR_CREATE_FROM_CITY_CHANGE',
          messageTemplateKey: 'create_new_place_for_changed_city',
          uiTitle: `Create place for ${newCityName}?`,
          uiMessage: `You changed the city for "${accommodationName}" to ${newCityName}. Would you like to create a new place entry for ${newCityName}?`,
          data: {
            newCityName,
            oldCityName: oldCity,
            country: countryName,
            accommodationId,
            tripId,
            userId,
            accommodationName,
            needsCreation: true,
            placeCreationData: {
              name: newCityName,
              fullName: countryName ? `${newCityName}, ${countryName}` : newCityName,
              type: 'city',
              source: 'accommodation_city_change',
              sourceId: accommodationId
            },
            oldPlaceReference,
            oldPlaceHasOtherReferences: false, // Conservative assumption
            suggestOldPlaceReview: oldPlaceReference, // Suggest review if there was an old place
            suggestOldPlaceCleanup: oldPlaceReference && !oldPlaceHasOtherReferences
          }
        };
      }
    },
    notificationStrategy: 'immediate_prompt',
    priority: 'high'
  },
  {
    ruleName: 'SuggestUnlinkAccommodationFromPlace',
    eventType: EVENT_TYPES.ACCOMMODATION_CITY_CHANGED,
    /**
         * @param {Object} eventData - The event data for ACCOMMODATION_CITY_CHANGED.
         * @param {Object} context - Context object, contains { placesService }.
         * @returns {Promise<boolean>} - True if conditions are met, false otherwise.
         */
    conditions: async (eventData, context) => {
      // Condition: New city must be empty/null (city was cleared)
      const newCityEmpty = !eventData.details?.city || eventData.details.city.trim() === '';
      if (!newCityEmpty) {
        console.log('Rule \'SuggestUnlinkAccommodationFromPlace\': New city is not empty, skipping.', eventData);
        return false;
      }
            
      // Condition: Must have had a previous place reference (something to unlink from)
      const hadPreviousPlace = eventData.oldPlaceReference && eventData.oldPlaceReference.placeId;
      if (!hadPreviousPlace) {
        console.log('Rule \'SuggestUnlinkAccommodationFromPlace\': No previous place reference, skipping.', eventData);
        return false;
      }
            
      // Condition: Event must have tripId and userId
      if (!eventData.tripId || !eventData.userId) {
        console.log('Rule \'SuggestUnlinkAccommodationFromPlace\': Missing tripId or userId, skipping.', eventData);
        return false;
      }
            
      console.log('Rule \'SuggestUnlinkAccommodationFromPlace\': Conditions met - city cleared and had previous place reference:', eventData);
      return true;
    },
    /**
         * @param {Object} eventData - The event data for ACCOMMODATION_CITY_CHANGED.
         * @param {Object} context - Context object, contains { placesService }.
         * @returns {Promise<Object>} - Object containing suggestionType, messageTemplateKey, and data for the suggestion.
         */
    generateSuggestionDetails: async (eventData, context) => {
      const { id: accommodationId, tripId, userId, summary, oldCity, oldPlaceReference } = eventData;
      const accommodationName = summary || eventData.details?.name || 'Accommodation';

      console.log(`Rule 'SuggestUnlinkAccommodationFromPlace': Processing city removal - was "${oldCity}"`);

      try {
        // Check if old place is now orphaned after unlinking
        // NOTE: The accommodation has already been unlinked when this event is published,
        // so we can directly check if the place usage count is 0
        let oldPlaceIsOrphaned = false;
        if (oldPlaceReference?.placeId) {
          const oldPlaceUsageCount = await context.placesService.getPlaceUsageCount(userId, tripId, oldPlaceReference.placeId);
          // Since the accommodation has already been unlinked, count of 0 means orphaned
          oldPlaceIsOrphaned = oldPlaceUsageCount === 0;
          console.log(`Rule 'SuggestUnlinkAccommodationFromPlace': Old place ${oldPlaceReference.placeId} has ${oldPlaceUsageCount} references (is orphaned: ${oldPlaceIsOrphaned})`);
        }

        // Only show notification if the place is actually orphaned
        if (!oldPlaceIsOrphaned) {
          console.log('Rule \'SuggestUnlinkAccommodationFromPlace\': Old place still has references, not showing notification');
          return null; // Don't generate suggestion if place still has references
        }

        // Generate informational notification (same pattern as orphaned place notifications)
        return {
          suggestionType: 'PLACE_ORPHANED_NOTIFICATION',
          messageTemplateKey: 'city_removed_place_notification',
          uiDetails: {
            title: `Review old place: ${oldCity}`,
            message: `The place "${oldCity}" is no longer referenced by any accommodation. You can remove it from your trip if you no longer need it.`
          },
          data: {
            accommodationId,
            tripId,
            userId,
            accommodationName,
            oldPlaceId: oldPlaceReference.placeId,
            oldCityName: oldCity,
            oldPlaceIsOrphaned
          }
        };
      } catch (error) {
        console.error('Rule \'SuggestUnlinkAccommodationFromPlace\': Error processing city removal:', error);
        // Don't show fallback notification - if we can't check, don't notify
        console.log('Rule \'SuggestUnlinkAccommodationFromPlace\': Error checking place usage, not showing notification');
        return null;
      }
    },
    notificationStrategy: 'immediate_prompt',
    priority: 'medium'
  },
  {
    ruleName: 'HandleAccommodationDeletion',
    eventType: EVENT_TYPES.ACCOMMODATION_DELETED,
    /**
         * @param {Object} eventData - The event data for ACCOMMODATION_DELETED.
         * @param {Object} context - Context object, contains { placesService }.
         * @returns {Promise<boolean>} - True if conditions are met, false otherwise.
         */
    conditions: async (eventData, context) => {
      // Condition: Must have had a place reference (something that could become orphaned)
      const hadPlaceReference = eventData.placeReference && eventData.placeReference.placeId;
      if (!hadPlaceReference) {
        console.log('Rule \'HandleAccommodationDeletion\': No place reference in deleted accommodation, skipping.', eventData);
        return false;
      }
            
      // Condition: Event must have tripId and userId for place analysis
      if (!eventData.tripId || !eventData.userId) {
        console.log('Rule \'HandleAccommodationDeletion\': Missing tripId or userId in eventData, skipping.', eventData);
        return false;
      }
            
      console.log('Rule \'HandleAccommodationDeletion\': Conditions met - deleted accommodation had place reference:', eventData);
      return true;
    },
    /**
         * @param {Object} eventData - The event data for ACCOMMODATION_DELETED.
         * @param {Object} context - Context object, contains { placesService }.
         * @returns {Promise<Object>} - Object containing suggestionType, messageTemplateKey, and data for the suggestion.
         */
    generateSuggestionDetails: async (eventData, context) => {
      const { id: accommodationId, tripId, userId, summary, placeReference, oldCity } = eventData;
      const accommodationName = summary || eventData.details?.name || 'Deleted Accommodation';
      const cityName = oldCity || eventData.details?.city || 'Unknown City';

      console.log(`Rule 'HandleAccommodationDeletion': Processing accommodation deletion - was linked to "${cityName}"`);

      try {
        // Check if the place is now orphaned after accommodation deletion
        // NOTE: The accommodation has already been deleted when this event is published,
        // so we can directly check if the place usage count is 0
        let placeIsOrphaned = false;
        if (placeReference?.placeId) {
          const placeUsageCount = await context.placesService.getPlaceUsageCount(userId, tripId, placeReference.placeId);
          // Since the accommodation has already been deleted, count of 0 means orphaned
          placeIsOrphaned = placeUsageCount === 0;
          console.log(`Rule 'HandleAccommodationDeletion': Place ${placeReference.placeId} has ${placeUsageCount} references (is orphaned: ${placeIsOrphaned})`);
        }

        // Only show notification if the place is actually orphaned
        if (!placeIsOrphaned) {
          console.log('Rule \'HandleAccommodationDeletion\': Place still has references, not showing notification');
          return null; // Don't generate suggestion if place still has references
        }

        // Generate informational notification (same pattern as other orphaned place notifications)
        return {
          suggestionType: 'PLACE_ORPHANED_NOTIFICATION',
          messageTemplateKey: 'accommodation_deleted_place_notification',
          uiDetails: {
            title: `Review place: ${cityName}`,
            message: `The place "${cityName}" is no longer referenced by any accommodation after deleting "${accommodationName}". You can remove it from your trip if you no longer need it.`
          },
          data: {
            accommodationId,
            tripId,
            userId,
            accommodationName,
            placeId: placeReference.placeId,
            cityName,
            placeIsOrphaned,
            trigger: 'accommodation_deletion'
          }
        };
      } catch (error) {
        console.error('Rule \'HandleAccommodationDeletion\': Error processing accommodation deletion:', error);
        // Don't show fallback notification - if we can't check, don't notify
        console.log('Rule \'HandleAccommodationDeletion\': Error checking place usage, not showing notification');
        return null;
      }
    },
    notificationStrategy: 'immediate_prompt',
    priority: 'medium'
  },
    
  // =====================================================
  // ACCOMMODATION-DAY INTEGRATION RULES - Phase 2A
  // =====================================================
    
  {
    ruleName: 'SuggestDayEntriesForAccommodation',
    eventType: [EVENT_TYPES.ACCOMMODATION_ADDED, EVENT_TYPES.ACCOMMODATION_UPDATED],
    /**
         * Determines whether an accommodation qualifies for day entry suggestions.
         * 
         * This unified rule triggers when accommodations are added or updated and evaluates
         * whether the accommodation has sufficient information to generate meaningful
         * day entry suggestions. For updates, it specifically checks if dates have changed.
         * 
         * The rule checks for essential data requirements:
         * - Check-in date (required for any day integration)
         * - User and trip identification (required for data operations)
         * - Display information (name, city, or summary for content generation)
         * - For updates: Date changes must have occurred
         * 
         * The rule is designed to be conservative - only accommodations with complete
         * date and identification information will trigger day entry suggestions.
         * This prevents incomplete or malformed data from creating poor user experiences.
         * 
         * @param {Object} eventData - The accommodation event data (added or updated)
         * @param {Object} context - Service context containing daysService and other utilities
         * @returns {Promise<boolean>} True if accommodation qualifies for day suggestions
         */
    conditions: async (eventData, context) => {
      console.log('=== DAYS RULE CONDITIONS START ===');
      console.log('Rule \'SuggestDayEntriesForAccommodation\': Checking conditions with eventData:', JSON.stringify(eventData, null, 2));
      console.log('Rule \'SuggestDayEntriesForAccommodation\': Context daysService available:', !!context.daysService);
      console.log('Rule \'SuggestDayEntriesForAccommodation\': Context daysService type:', typeof context.daysService);
      console.log('Rule \'SuggestDayEntriesForAccommodation\': Event type:', eventData.eventType);
      
      // For ACCOMMODATION_UPDATED events, check if dates actually changed
      if (eventData.eventType === EVENT_TYPES.ACCOMMODATION_UPDATED) {
        console.log('Rule \'SuggestDayEntriesForAccommodation\': Processing ACCOMMODATION_UPDATED event');
        
        // Must have old and new accommodation data for comparison
        if (!eventData.oldAccommodation || (!eventData.newAccommodation && !eventData.details)) {
          console.log('Rule \'SuggestDayEntriesForAccommodation\': CONDITION FAILED - Missing old/new accommodation data for date change detection');
          console.log('=== DAYS RULE CONDITIONS END (FAILED) ===');
          return false;
        }
        
        // Compare old and new dates to detect changes
        const oldDates = eventData.oldAccommodation?.details;
        const newDates = eventData.newAccommodation?.details || eventData.details;
        
        const oldCheckIn = oldDates?.checkInDate;
        const oldCheckOut = oldDates?.checkOutDate;
        const newCheckIn = newDates?.checkInDate;
        const newCheckOut = newDates?.checkOutDate;
        
        // Trigger if any date changed
        const datesChanged = oldCheckIn !== newCheckIn || oldCheckOut !== newCheckOut;
        
        console.log('Rule \'SuggestDayEntriesForAccommodation\': Date change analysis:', {
          oldCheckIn, newCheckIn,
          oldCheckOut, newCheckOut,
          datesChanged
        });
        
        if (!datesChanged) {
          console.log('Rule \'SuggestDayEntriesForAccommodation\': CONDITION FAILED - No date changes detected, no day entry updates needed');
          console.log('=== DAYS RULE CONDITIONS END (FAILED) ===');
          return false;
        }
        
        console.log('Rule \'SuggestDayEntriesForAccommodation\': Date changes detected, proceeding with validation');
      }
      
      // Extract accommodation details based on event type
      const accommodationDetails = eventData.eventType === EVENT_TYPES.ACCOMMODATION_UPDATED 
        ? (eventData.newAccommodation?.details || eventData.details)
        : eventData.details;
      
      const accommodationSummary = eventData.eventType === EVENT_TYPES.ACCOMMODATION_UPDATED 
        ? (eventData.newAccommodation?.summary || eventData.summary)
        : eventData.summary;
            
      // Primary requirement: Accommodation must have check-in date for day integration
      const hasCheckInDate = accommodationDetails?.checkInDate;
      console.log('Rule \'SuggestDayEntriesForAccommodation\': Check-in date check - hasCheckInDate:', hasCheckInDate);
      if (!hasCheckInDate) {
        console.log('Rule \'SuggestDayEntriesForAccommodation\': CONDITION FAILED - No check-in date found, day integration not possible.');
        console.log('=== DAYS RULE CONDITIONS END (FAILED) ===');
        return false;
      }
            
      // Security requirement: Event must have user and trip identification for database operations
      console.log('Rule \'SuggestDayEntriesForAccommodation\': ID check - tripId:', eventData.tripId, 'userId:', eventData.userId);
      if (!eventData.tripId || !eventData.userId) {
        console.log('Rule \'SuggestDayEntriesForAccommodation\': CONDITION FAILED - Missing tripId or userId, cannot perform day operations.');
        console.log('=== DAYS RULE CONDITIONS END (FAILED) ===');
        return false;
      }
            
      // Content requirement: Must have accommodation name, city, or summary for meaningful day content
      const hasDisplayInfo = accommodationDetails?.name || accommodationDetails?.city || accommodationSummary;
      console.log('Rule \'SuggestDayEntriesForAccommodation\': Display info check - name:', accommodationDetails?.name, 'city:', accommodationDetails?.city, 'summary:', accommodationSummary);
      console.log('Rule \'SuggestDayEntriesForAccommodation\': hasDisplayInfo:', hasDisplayInfo);
      if (!hasDisplayInfo) {
        console.log('Rule \'SuggestDayEntriesForAccommodation\': CONDITION FAILED - No display information available, cannot generate meaningful day content.');
        console.log('=== DAYS RULE CONDITIONS END (FAILED) ===');
        return false;
      }
            
      console.log('Rule \'SuggestDayEntriesForAccommodation\': CONDITIONS PASSED - All conditions met, proceeding with day entry analysis');
      console.log('=== DAYS RULE CONDITIONS END (PASSED) ===');
      return true;
    },
    /**
         * Generates day entry suggestions for qualified accommodations.
         * 
         * This method performs comprehensive analysis of accommodation date information
         * and existing day entries to determine the appropriate suggestion strategy.
         * The process involves several key steps:
         * 
         * 1. Date Validation: Ensures accommodation dates are logically consistent
         * 2. Range Calculation: Determines which dates should have day entries
         * 3. Day Existence Check: Identifies which required days already exist vs. missing
         * 4. Content Generation: Creates appropriate day entry content with context
         * 5. Suggestion Strategy: Chooses between day creation, missing day notification, or content addition
         * 
         * The method handles multiple accommodation stay patterns:
         * - Single day stays (check-in only or same-day check-in/out)
         * - Multi-day stays with check-in and check-out dates
         * - Incomplete day coverage (some days exist, others missing)
         * - Complete missing day coverage (no days exist for accommodation dates)
         * 
         * Content generation is context-aware, adding appropriate indicators like
         * "Check-in", "Staying", and "Check-out" based on the day's role in the stay.
         * 
         * @param {Object} eventData - The accommodation creation event data
         * @param {Object} context - Service context containing daysService and placesService
         * @returns {Promise<Object>} Suggestion details or null if no suggestion needed
         */
    generateSuggestionDetails: async (eventData, context) => {
      console.log('=== DAYS RULE GENERATE SUGGESTION START ===');
      const { id: accommodationId, tripId, userId } = eventData;
      
      // Extract accommodation data based on event type
      let currentAccommodation, accommodationDetails, accommodationName;
      let isDateChangeEvent = false;
      let oldAccommodation = null;
      
      if (eventData.eventType === EVENT_TYPES.ACCOMMODATION_UPDATED) {
        console.log('Rule \'SuggestDayEntriesForAccommodation\': Processing ACCOMMODATION_UPDATED event');
        isDateChangeEvent = true;
        oldAccommodation = eventData.oldAccommodation;
        currentAccommodation = eventData.newAccommodation || {
          id: accommodationId,
          details: eventData.details,
          summary: eventData.summary || eventData.details?.name || 'Accommodation'
        };
        accommodationDetails = currentAccommodation.details;
        accommodationName = currentAccommodation.summary || accommodationDetails?.name || 'Accommodation';
      } else {
        console.log('Rule \'SuggestDayEntriesForAccommodation\': Processing ACCOMMODATION_ADDED event');
        currentAccommodation = eventData;
        accommodationDetails = eventData.details;
        accommodationName = eventData.details?.name || eventData.summary || 'Accommodation';
      }
            
      console.log(`Rule 'SuggestDayEntriesForAccommodation': Analyzing accommodation "${accommodationName}" for day entry integration`);
      console.log('Rule \'SuggestDayEntriesForAccommodation\': Event type:', eventData.eventType);
      console.log('Rule \'SuggestDayEntriesForAccommodation\': Is date change event:', isDateChangeEvent);
      console.log('Rule \'SuggestDayEntriesForAccommodation\': Context services available:');
      console.log('  - daysService:', !!context.daysService, typeof context.daysService);
      console.log('  - placesService:', !!context.placesService, typeof context.placesService);

      try {
        console.log('Rule \'SuggestDayEntriesForAccommodation\': STEP 1 - Attempting to import accommodationDayUtils');
        // Import specialized day creation utilities designed for accommodation integration
        // (Already imported at top of file for Phase 2B compatibility)
                
        console.log('Rule \'SuggestDayEntriesForAccommodation\': STEP 1 SUCCESS - Import successful, checking function availability:');
        console.log('  - calculateAccommodationDateRangeForDaysSuggestions:', typeof calculateAccommodationDateRangeForDaysSuggestions);
        console.log('  - generateDayContentForAccommodationSuggestions:', typeof generateDayContentForAccommodationSuggestions);
        console.log('  - findMissingDaysForAccommodationDates:', typeof findMissingDaysForAccommodationDates);
        console.log('  - validateAccommodationDatesForDayCreation:', typeof validateAccommodationDatesForDayCreation);

        console.log('Rule \'SuggestDayEntriesForAccommodation\': STEP 2 - Validating accommodation dates');
        // Step 1: Validate accommodation dates using creation-specific validation
        const validation = validateAccommodationDatesForDayCreation(accommodationDetails);
        console.log('Rule \'SuggestDayEntriesForAccommodation\': STEP 2 RESULT - Validation result:', JSON.stringify(validation, null, 2));
                
        if (!validation.isValid || !validation.canCreateDays) {
          console.warn(`Rule 'SuggestDayEntriesForAccommodation': STEP 2 FAILED - Date validation failed for accommodation ${accommodationId}:`, validation.errors);
          console.log('=== DAYS RULE GENERATE SUGGESTION END (VALIDATION FAILED) ===');
          return null; // Skip suggestion generation for invalid dates
        }
        console.log('Rule \'SuggestDayEntriesForAccommodation\': STEP 2 SUCCESS - Date validation passed');

        console.log('Rule \'SuggestDayEntriesForAccommodation\': STEP 3 - Calculating accommodation date range');
        // Step 2: Calculate accommodation date range using suggestion-optimized calculation
        const dateRange = calculateAccommodationDateRangeForDaysSuggestions(currentAccommodation);
        console.log('Rule \'SuggestDayEntriesForAccommodation\': STEP 3 RESULT - Date range calculation result:', JSON.stringify(dateRange, null, 2));
                
        if (!dateRange || dateRange.dates.length === 0) {
          console.log('Rule \'SuggestDayEntriesForAccommodation\': STEP 3 FAILED - No valid date range calculated, cannot generate day suggestions');
          console.log('=== DAYS RULE GENERATE SUGGESTION END (DATE RANGE FAILED) ===');
          return null;
        }
        console.log(`Rule 'SuggestDayEntriesForAccommodation': STEP 3 SUCCESS - Calculated ${dateRange.dates.length} days for accommodation stay (${dateRange.startDate} to ${dateRange.endDate})`);

        console.log('Rule \'SuggestDayEntriesForAccommodation\': STEP 4 - Analyzing existing day coverage');
        console.log('Rule \'SuggestDayEntriesForAccommodation\': STEP 4 - Calling findMissingDaysForAccommodationDates with:');
        console.log('  - userId:', userId);
        console.log('  - tripId:', tripId);
        console.log('  - dates:', JSON.stringify(dateRange.dates));
        console.log('  - daysService type:', typeof context.daysService);
        console.log('  - daysService is mock:', context.daysService && context.daysService.isMockService);
                
        // Step 3: Analyze existing day coverage to determine missing days
        const missingDayAnalysis = await findMissingDaysForAccommodationDates(
          userId, 
          tripId, 
          dateRange.dates, 
          context.daysService
        );
                
        console.log('Rule \'SuggestDayEntriesForAccommodation\': STEP 4 RESULT - Missing day analysis:', JSON.stringify(missingDayAnalysis, null, 2));

        console.log('Rule \'SuggestDayEntriesForAccommodation\': STEP 5 - Checking if all days are missing');
        // Step 4: Handle case where no days exist for accommodation dates
        // This indicates the user hasn't created their daily itinerary yet
        if (missingDayAnalysis.needsNotification && missingDayAnalysis.missingDates.length === dateRange.dates.length) {
          console.log('Rule \'SuggestDayEntriesForAccommodation\': STEP 5 MATCHED - No day entries exist for accommodation dates, suggesting day creation first');
          
          // Get user's Days list date range for informative message
          let daysListInfo = '';
          try {
            const allDays = await context.daysService.fetchDays(userId, tripId);
            const daysDateRange = calculateDaysListDateRange(allDays);
            
            if (daysDateRange) {
              const startDateFormatted = formatDateForDisplay(daysDateRange.startDate);
              const endDateFormatted = formatDateForDisplay(daysDateRange.endDate);
              daysListInfo = `but the Days list covers dates ${startDateFormatted} to ${endDateFormatted}`;
            } else {
              daysListInfo = `but no Days exist yet`;
            }
          } catch (error) {
            console.warn('Could not retrieve Days list for informative message:', error);
            daysListInfo = `but these dates don't exist in your list of Days`;
          }
          
          const accommodationStartFormatted = formatDateForDisplay(dateRange.startDate);
          const accommodationEndFormatted = dateRange.endDate !== dateRange.startDate ? ` to ${formatDateForDisplay(dateRange.endDate)}` : '';
                    
          const suggestion = {
            suggestionType: 'MISSING_DAYS_NOTIFICATION',
            messageTemplateKey: 'accommodation_dates_no_matching_days',
            uiDetails: {
              title: 'Accommodation dates don\'t match the Days list dates',
              message: `Your accommodation "${accommodationName}" has dates ${accommodationStartFormatted}${accommodationEndFormatted}, ${daysListInfo}. Would you like to create day entries for these dates first?`
            },
            data: {
              accommodationId,
              tripId,
              userId,
              accommodationName,
              missingDates: missingDayAnalysis.missingDates,
              dateRange,
              totalMissingDays: missingDayAnalysis.missingDates.length,
              stayType: dateRange.stayType
            }
          };
          console.log('Rule \'SuggestDayEntriesForAccommodation\': STEP 5 SUGGESTION:', JSON.stringify(suggestion, null, 2));
          console.log('=== DAYS RULE GENERATE SUGGESTION END (MISSING DAYS NOTIFICATION) ===');
          return suggestion;
        }

        // Step 5: Handle case where SOME days are missing (partial coverage)
        // Don't create partial entries - inform user to create missing days first
        if (missingDayAnalysis.needsNotification && missingDayAnalysis.missingDates.length > 0) {
          console.log('Rule \'SuggestDayEntriesForAccommodation\': STEP 5 PARTIAL MATCH - Some days missing, suggesting complete day creation first');
          
          // Get user's Days list date range for informative message
          let daysListInfo = '';
          try {
            const allDays = await context.daysService.fetchDays(userId, tripId);
            const daysDateRange = calculateDaysListDateRange(allDays);
            
            if (daysDateRange) {
              const startDateFormatted = formatDateForDisplay(daysDateRange.startDate);
              const endDateFormatted = formatDateForDisplay(daysDateRange.endDate);
              daysListInfo = `but the Days list covers dates ${startDateFormatted} to ${endDateFormatted}`;
            } else {
              daysListInfo = `but no Days exist yet`;
            }
          } catch (error) {
            console.warn('Could not retrieve Days list for informative message:', error);
            daysListInfo = `but these dates don't exist in your list of Days`;
          }
          
          const accommodationStartFormatted = formatDateForDisplay(dateRange.startDate);
          const accommodationEndFormatted = dateRange.endDate !== dateRange.startDate ? ` to ${formatDateForDisplay(dateRange.endDate)}` : '';
          
          const suggestion = {
            suggestionType: 'MISSING_DAYS_NOTIFICATION',
            messageTemplateKey: 'accommodation_dates_no_matching_days',
            uiDetails: {
              title: 'Accommodation dates don\'t match the Days list dates',
              message: `Your accommodation "${accommodationName}" has dates ${accommodationStartFormatted}${accommodationEndFormatted}, ${daysListInfo}. You may wish to fix this by changing the accommodation dates or adding the missing dates to your Days list.`
            },
            data: {
              accommodationId,
              tripId,
              userId,
              accommodationName,
              missingDates: missingDayAnalysis.missingDates,
              existingDays: missingDayAnalysis.existingDays,
              dateRange,
              totalMissingDays: missingDayAnalysis.missingDates.length,
              stayType: dateRange.stayType
            }
          };
          console.log('Rule \'SuggestDayEntriesForAccommodation\': STEP 5 PARTIAL SUGGESTION:', JSON.stringify(suggestion, null, 2));
          console.log('=== DAYS RULE GENERATE SUGGESTION END (PARTIAL MISSING DAYS NOTIFICATION) ===');
          return suggestion;
        }

        console.log('Rule \'SuggestDayEntriesForAccommodation\': STEP 5 - No days missing, continuing to day entry generation');

        console.log('Rule \'SuggestDayEntriesForAccommodation\': STEP 6 - Generating day entry content for existing days');
        // Step 5: Generate day entry content for existing days
        const dayEntries = [];
        for (const date of dateRange.dates) {
          console.log(`Rule 'SuggestDayEntriesForAccommodation': STEP 6 - Processing date ${date}`);
          const existingDay = missingDayAnalysis.existingDays.find(day => day.date === date);
          console.log(`Rule 'SuggestDayEntriesForAccommodation': STEP 6 - Found existing day for ${date}:`, !!existingDay);
                    
          if (existingDay) {
            console.log('Rule \'SuggestDayEntriesForAccommodation\': STEP 6 - Existing day details:', JSON.stringify(existingDay, null, 2));
            // Generate context-aware content using specialized content generation
            const content = generateDayContentForAccommodationSuggestions(
              eventData, 
              date, 
              dateRange.dates
            );
            console.log(`Rule 'SuggestDayEntriesForAccommodation': STEP 6 - Generated content for ${date}:`, content);
                        
            dayEntries.push({
              date,
              dayId: existingDay.dayId,
              content,
              hasExistingContent: existingDay.hasContent
            });
            console.log(`Rule 'SuggestDayEntriesForAccommodation': STEP 6 - Added day entry for ${date}`);
          } else {
            console.log(`Rule 'SuggestDayEntriesForAccommodation': STEP 6 - No existing day found for ${date}, skipping`);
          }
        }
        console.log(`Rule 'SuggestDayEntriesForAccommodation': STEP 6 RESULT - Generated ${dayEntries.length} day entries:`, JSON.stringify(dayEntries, null, 2));

        console.log('Rule \'SuggestDayEntriesForAccommodation\': STEP 7 - Checking if any day entries were generated');
        // Step 6: Handle case where no existing days can accommodate accommodation content
        if (dayEntries.length === 0) {
          console.log('Rule \'SuggestDayEntriesForAccommodation\': STEP 7 MATCHED - No suitable existing days found for accommodation integration');
          
          // Get user's Days list date range for informative message
          let daysListInfo = '';
          try {
            const allDays = await context.daysService.fetchDays(userId, tripId);
            const daysDateRange = calculateDaysListDateRange(allDays);
            
            if (daysDateRange) {
              const startDateFormatted = formatDateForDisplay(daysDateRange.startDate);
              const endDateFormatted = formatDateForDisplay(daysDateRange.endDate);
              daysListInfo = `but the Days list covers dates ${startDateFormatted} to ${endDateFormatted}`;
            } else {
              daysListInfo = `but no Days exist yet`;
            }
          } catch (error) {
            console.warn('Could not retrieve Days list for informative message:', error);
            daysListInfo = `but no day entries exist yet`;
          }
          
          const accommodationStartFormatted = formatDateForDisplay(dateRange.startDate);
          const accommodationEndFormatted = dateRange.endDate !== dateRange.startDate ? ` to ${formatDateForDisplay(dateRange.endDate)}` : '';
                    
          const suggestion = {
            suggestionType: 'MISSING_DAYS_NOTIFICATION',
            messageTemplateKey: 'accommodation_dates_no_existing_days',
            uiDetails: {
              title: 'Accommodation dates don\'t match the Days list dates',
              message: `Your accommodation "${accommodationName}" has dates ${accommodationStartFormatted}${accommodationEndFormatted}, ${daysListInfo}. Create day entries first to link accommodation to your itinerary.`
            },
            data: {
              accommodationId,
              tripId,
              userId,
              accommodationName,
              missingDates: dateRange.dates,
              dateRange,
              stayType: dateRange.stayType
            }
          };
          console.log('Rule \'SuggestDayEntriesForAccommodation\': STEP 7 SUGGESTION:', JSON.stringify(suggestion, null, 2));
          console.log('=== DAYS RULE GENERATE SUGGESTION END (NO EXISTING DAYS) ===');
          return suggestion;
        }
        console.log('Rule \'SuggestDayEntriesForAccommodation\': STEP 7 - Day entries found, proceeding to final suggestion');

        console.log('Rule \'SuggestDayEntriesForAccommodation\': STEP 8 - Generating final day entry integration suggestion');
        
        // Step 7: Generate successful day entry integration suggestion
        const isMultiDay = dateRange.stayType === 'MULTI_DAY';
        
        // Prepare operations list for date change events
        const operations = [];
        
        // For date change events, add removal operation first
        if (isDateChangeEvent && oldAccommodation?.details?.checkInDate) {
          operations.push({
            type: 'REMOVE_OLD_ENTRIES',
            accommodation: oldAccommodation,
            description: `Remove old entries for "${oldAccommodation.summary || accommodationName}"`
          });
          console.log('🗑️ [SuggestionRules] SuggestDayEntriesForAccommodation: Will remove old day entries for date change');
        }
        
        // Add the new entries operation
        operations.push({
          type: 'ADD_NEW_ENTRIES',
          accommodation: currentAccommodation,
          description: `Add new entries for "${accommodationName}"`
        });
        
        // Generate appropriate UI message based on event type
        let uiMessage, uiTitle;
        if (isDateChangeEvent) {
          uiTitle = 'Update accommodation dates?';
          const operationDescriptions = operations.map(op => op.description).join(' and ');
          uiMessage = `Update your day entries for "${accommodationName}"? This will ${operationDescriptions.toLowerCase()}.`;
        } else {
          uiTitle = 'Add accommodation to daily itinerary';
          uiMessage = isMultiDay 
            ? `Add "${accommodationName}" to your daily itinerary for ${dayEntries.length} days (${dateRange.startDate} to ${dateRange.endDate})?`
            : `Add "${accommodationName}" to your daily itinerary for ${dateRange.startDate}?`;
        }

        console.log(`Rule 'SuggestDayEntriesForAccommodation': STEP 8 - Generated suggestion for ${dayEntries.length} day entries`);
        console.log(`Rule 'SuggestDayEntriesForAccommodation': STEP 8 - isMultiDay: ${isMultiDay}`);
        console.log(`Rule 'SuggestDayEntriesForAccommodation': STEP 8 - isDateChangeEvent: ${isDateChangeEvent}`);
        console.log(`Rule 'SuggestDayEntriesForAccommodation': STEP 8 - operations: ${operations.length}`);
        console.log(`Rule 'SuggestDayEntriesForAccommodation': STEP 8 - uiMessage: ${uiMessage}`);

        const finalSuggestion = {
          suggestionType: 'DAY_ENTRIES_FOR_ACCOMMODATION',
          messageTemplateKey: isDateChangeEvent 
            ? 'accommodation_date_change_unified' 
            : (isMultiDay ? 'suggest_day_entries_multi_day' : 'suggest_day_entries_single_day'),
          uiDetails: {
            title: uiTitle,
            message: uiMessage
          },
          data: {
            accommodationId,
            tripId,
            userId,
            accommodationName,
            dayEntries,
            dateRange,
            stayType: dateRange.stayType,
            totalDays: dayEntries.length,
            hasPartialDayCoverage: missingDayAnalysis.missingDates.length > 0,
            // Add data for date change handling
            isDateChangeEvent,
            operations: operations.length > 0 ? operations : undefined,
            oldAccommodation: isDateChangeEvent ? oldAccommodation : undefined,
            newAccommodation: isDateChangeEvent ? currentAccommodation : undefined
          }
        };
        console.log('Rule \'SuggestDayEntriesForAccommodation\': STEP 8 FINAL SUGGESTION:', JSON.stringify(finalSuggestion, null, 2));
        console.log('=== DAYS RULE GENERATE SUGGESTION END (SUCCESS) ===');
        return finalSuggestion;
                
      } catch (error) {
        console.error('Rule \'SuggestDayEntriesForAccommodation\': ERROR CAUGHT - Error during suggestion analysis:', error);
        console.error('Rule \'SuggestDayEntriesForAccommodation\': ERROR STACK:', error.stack);
        console.log('=== DAYS RULE GENERATE SUGGESTION END (ERROR) ===');
        // Return null to prevent malformed suggestions from disrupting user experience
        return null;
      }
    },
    notificationStrategy: 'immediate_prompt',
    priority: 'high'
  },



  /**
   * Phase 2B: Accommodation Deletion Day Cleanup Rule
   * 
   * This rule handles cleanup of day entries when an accommodation is deleted.
   * It removes all "Stay at..." content blocks that reference the deleted accommodation.
   */
  {
    ruleName: 'CleanupDaysOnAccommodationDeletion',
    eventType: EVENT_TYPES.ACCOMMODATION_DELETED,
    /**
     * @param {Object} eventData - The event data for ACCOMMODATION_DELETED.
     * @param {Object} context - Context object, contains { daysService, accommodationService }.
     * @returns {Promise<boolean>} - True if accommodation had content blocks to clean up.
     */
    conditions: async (eventData, context) => {
      console.log('🔍 [SuggestionRules] CleanupDaysOnAccommodationDeletion: Checking conditions', {
        hasUserId: !!eventData.userId,
        hasTripId: !!eventData.tripId,
        hasAccommodationId: !!eventData.id,
        hasAccommodationData: !!eventData.accommodation
      });
      
      // Must have required identifiers
      if (!eventData.userId || !eventData.tripId || !eventData.id) {
        console.log('❌ [SuggestionRules] CleanupDaysOnAccommodationDeletion: Missing required identifiers, skipping');
        return false;
      }

      // Accommodation data should be available in various fields based on the deletion event structure
      const hasAccommodationData = eventData.summary || eventData.details || eventData.type;
      if (!hasAccommodationData) {
        console.log('❌ [SuggestionRules] CleanupDaysOnAccommodationDeletion: Missing accommodation data, skipping');
        return false;
      }

      console.log('✅ [SuggestionRules] CleanupDaysOnAccommodationDeletion: Conditions met for deletion cleanup');
      return true;
    },
    /**
     * @param {Object} eventData - The event data for ACCOMMODATION_DELETED.
     * @param {Object} context - Context object, contains { daysService, accommodationService }.
     * @returns {Promise<Object>} - Suggestion details for accommodation deletion cleanup.
     */
    generateSuggestionDetails: async (eventData, context) => {
      const { id: accommodationId, tripId, userId } = eventData;
      const accommodationName = eventData.summary || eventData.details?.name || 'Deleted accommodation';
      
      console.log('🗑️ [SuggestionRules] CleanupDaysOnAccommodationDeletion: Processing deletion cleanup', {
        accommodationId,
        accommodationName,
        hasCheckInDate: !!eventData.details?.checkInDate,
        hasCheckOutDate: !!eventData.details?.checkOutDate
      });

      try {
        // Get all days for this trip to check for accommodation references
        const allDays = await context.daysService.fetchDays(userId, tripId);
        
        console.log('🔍 [SuggestionRules] CleanupDaysOnAccommodationDeletion: Retrieved days for cleanup analysis', {
          totalDays: allDays.length
        });

        // Check each day and count how many have accommodation references
        let affectedDayCount = 0;
        let potentiallyAffectedDays = [];
        
        for (const day of allDays) {
          // Get day content to check for accommodation references
          try {
            const dayContent = await context.daysService.getDayContent(userId, tripId, day.id);
            const hasAccommodationRef = dayContent.some(block => 
              block.type === 'accommodation_reference' && 
              (block.accommodationId === accommodationId || 
               (block.accommodationId && block.accommodationId.startsWith('legacy_acc_')))
            );
            
            if (hasAccommodationRef) {
              affectedDayCount++;
              potentiallyAffectedDays.push({
                dayId: day.id,
                date: day.date,
                title: day.title || `Day ${day.order + 1}`
              });
            }
          } catch (error) {
            console.warn('⚠️ [SuggestionRules] CleanupDaysOnAccommodationDeletion: Could not check day content', {
              dayId: day.id,
              error: error.message
            });
          }
        }

        console.log('📊 [SuggestionRules] CleanupDaysOnAccommodationDeletion: Cleanup analysis complete', {
          totalDays: allDays.length,
          affectedDayCount,
          potentiallyAffectedDays: potentiallyAffectedDays.length
        });

        // If no affected days, no cleanup needed - but we still want to notify user
        if (affectedDayCount === 0) {
          console.log('ℹ️ [SuggestionRules] CleanupDaysOnAccommodationDeletion: No day entries to clean up');
          return {
            suggestionType: 'ACCOMMODATION_DELETION_NO_CLEANUP',
            uiDetails: {
              title: 'Accommodation deleted',
              message: `"${accommodationName}" was deleted. No day entries needed cleanup.`
            },
            data: {
              accommodationId,
              tripId,
              userId,
              accommodationName,
              affectedDayCount: 0,
              cleanupNeeded: false
            }
          };
        }

        const cleanupDescription = affectedDayCount === 1 
          ? `Remove "${accommodationName}" from 1 day entry`
          : `Remove "${accommodationName}" from ${affectedDayCount} day entries`;

        console.log('✅ [SuggestionRules] CleanupDaysOnAccommodationDeletion: Cleanup suggestion generated', {
          suggestionType: 'ACCOMMODATION_DELETION_CLEANUP',
          cleanupDescription,
          affectedDayCount
        });

        return {
          suggestionType: 'ACCOMMODATION_DELETION_CLEANUP',
          uiDetails: {
            title: 'Clean up day entries?',
            message: `"${accommodationName}" was deleted. ${cleanupDescription}?`
          },
          data: {
            accommodationId,
            tripId,
            userId,
            accommodationName,
            affectedDayCount,
            potentiallyAffectedDays,
            cleanupDescription,
            cleanupNeeded: true
          }
        };

      } catch (error) {
        console.error('❌ [SuggestionRules] CleanupDaysOnAccommodationDeletion: Error generating suggestion', {
          accommodationId,
          error: error.message,
          stack: error.stack
        });
        return null;
      }
    },
    notificationStrategy: 'immediate_prompt',
    priority: 'high'
  }
    
  // More rules can be added here for other event types or scenarios.
]; 